/**
 * EmailTemplateListPage — Email sablon kezelő admin oldal
 * Epic: Email Template System — Story 1.4
 *
 * Két-oszlopos layout: bal oldalsáv (sablon lista) + jobb panel (blokk szerkesztő)
 */

import type { VariableDefinition } from '@/api/email-templates';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@kgc/ui';
import { useRef, useState } from 'react';
import {
  useEmailTemplate,
  useEmailTemplateList,
  useUpdateEmailTemplate,
} from './hooks/useEmailTemplates';

// ============================================
// CONSTANTS
// ============================================

const LANGUAGES = ['hu', 'en', 'de', 'uk'] as const;
type Language = (typeof LANGUAGES)[number];

type BlockKey = 'greeting' | 'body' | 'cta';
const BLOCK_KEYS: BlockKey[] = ['greeting', 'body', 'cta'];

// ============================================
// VARIABLE DROPDOWN
// ============================================

interface VariableDropdownProps {
  variables: VariableDefinition[];
  onInsert: (key: string) => void;
}

function VariableDropdown({ variables, onInsert }: VariableDropdownProps) {
  const { t } = useTranslation();
  if (!variables.length) return null;
  return (
    <Select onValueChange={key => onInsert(key)}>
      <SelectTrigger className="w-[200px] h-8 text-xs">
        <SelectValue placeholder={t('email.insertVariable')} />
      </SelectTrigger>
      <SelectContent>
        {variables.map(v => (
          <SelectItem key={v.key} value={v.key}>
            {v.label} ({v.key})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ============================================
// BLOCK EDITOR ROW
// ============================================

interface BlockEditorRowProps {
  blockKey: BlockKey;
  content: string;
  editable: boolean;
  variables: VariableDefinition[];
  onChange: (key: BlockKey, value: string) => void;
}

function BlockEditorRow({ blockKey, content, editable, variables, onChange }: BlockEditorRowProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const BLOCK_LABELS: Record<BlockKey, string> = {
    greeting: t('email.greeting'),
    body: t('email.body'),
    cta: t('email.cta'),
  };

  function handleInsert(varKey: string) {
    const el = textareaRef.current;
    if (!el) {
      onChange(blockKey, content + `{{${varKey}}}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const inserted = `{{${varKey}}}`;
    const next = content.slice(0, start) + inserted + content.slice(end);
    onChange(blockKey, next);
    // Restore cursor after insertion
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + inserted.length, start + inserted.length);
    });
  }

  return (
    <Card className="mb-3">
      <CardHeader className="py-3 px-4 pb-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{BLOCK_LABELS[blockKey]}</CardTitle>
          {editable ? (
            <VariableDropdown variables={variables} onInsert={handleInsert} />
          ) : (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              🔒 {t('email.locked')}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-2 px-4 pb-3">
        {editable ? (
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={e => onChange(blockKey, e.target.value)}
            className="min-h-24 text-sm font-mono"
          />
        ) : (
          <div className="min-h-16 rounded bg-muted px-3 py-2 text-sm text-muted-foreground font-mono whitespace-pre-wrap">
            {content}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// MAIN PAGE
// ============================================

export function EmailTemplateListPage() {
  const { t } = useTranslation();

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('hu');

  const { data: templates, isLoading: listLoading } = useEmailTemplateList(selectedLanguage);
  const { data: selectedTemplate, isLoading: templateLoading } = useEmailTemplate(
    selectedSlug,
    selectedLanguage
  );

  // Local editable state — subject + blocks
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBlocks, setEditedBlocks] = useState<Partial<Record<BlockKey, string>>>({});

  const updateMutation = useUpdateEmailTemplate();

  // Sync local state when template loads
  const prevSlugRef = useRef<string | null>(null);
  if (selectedTemplate && selectedTemplate.slug !== prevSlugRef.current) {
    prevSlugRef.current = selectedTemplate.slug;
    setEditedSubject(selectedTemplate.subject);
    const blockMap: Partial<Record<BlockKey, string>> = {};
    for (const key of BLOCK_KEYS) {
      const block = selectedTemplate.blocks[key];
      if (block) blockMap[key] = block.content;
    }
    setEditedBlocks(blockMap);
  }

  // Required variables validation
  const missingRequired =
    selectedTemplate?.variables
      .filter(v => v.required)
      .filter(v => {
        const allContent = Object.values(editedBlocks).join(' ') + ' ' + editedSubject;
        return !allContent.includes(`{{${v.key}}}`);
      }) ?? [];

  async function handleSave() {
    if (!selectedTemplate || !selectedSlug) return;
    try {
      await updateMutation.mutateAsync({
        slug: selectedSlug,
        language: selectedLanguage,
        body: {
          subject: editedSubject,
          blocks: {
            greeting: { content: editedBlocks.greeting ?? '', editable: true },
            body: { content: editedBlocks.body ?? '', editable: true },
            ...(editedBlocks.cta !== undefined
              ? {
                  cta: {
                    content: editedBlocks.cta,
                    editable: true,
                    url: selectedTemplate.blocks.cta?.url ?? '',
                  },
                }
              : {}),
          },
        },
      });
      toast.success(t('email.saveSuccess'));
    } catch {
      toast.error(t('common.errorLabel'));
    }
  }

  function handleBlockChange(key: BlockKey, value: string) {
    setEditedBlocks(prev => ({ ...prev, [key]: value }));
  }

  function handleSelectTemplate(slug: string) {
    if (slug === selectedSlug) return;
    setSelectedSlug(slug);
    prevSlugRef.current = null; // force re-sync on load
    setEditedSubject('');
    setEditedBlocks({});
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('email.templates')}</h1>

      <div className="flex gap-6 items-start">
        {/* ── LEFT SIDEBAR ── */}
        <aside className="w-72 shrink-0">
          {/* Language tabs */}
          <div className="flex gap-1 mb-4">
            {LANGUAGES.map(lang => (
              <Button
                key={lang}
                variant={selectedLanguage === lang ? 'default' : 'outline'}
                size="sm"
                className="flex-1 uppercase text-xs"
                onClick={() => {
                  setSelectedLanguage(lang);
                  setSelectedSlug(null);
                  prevSlugRef.current = null;
                  setEditedSubject('');
                  setEditedBlocks({});
                }}
              >
                {lang}
              </Button>
            ))}
          </div>

          {/* Template list */}
          <Card>
            <CardHeader className="py-3 px-4 pb-2">
              <CardTitle className="text-sm">{t('email.templateList')}</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {listLoading ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {t('common.loading')}
                </p>
              ) : !templates?.length ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {t('email.selectTemplate')}
                </p>
              ) : (
                <div className="space-y-1">
                  {templates.map(tpl => (
                    <button
                      key={tpl.slug}
                      type="button"
                      onClick={() => handleSelectTemplate(tpl.slug)}
                      className={[
                        'w-full text-left rounded px-3 py-2 text-sm transition-colors',
                        selectedSlug === tpl.slug
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted',
                      ].join(' ')}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{tpl.name}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <Badge
                            variant={tpl.isCustomized ? 'default' : 'secondary'}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {tpl.isCustomized ? t('email.customized') : t('email.default')}
                          </Badge>
                          <span
                            className={`h-2 w-2 rounded-full ${tpl.isActive ? 'bg-green-500' : 'bg-muted-foreground'}`}
                            title={tpl.isActive ? t('email.active') : t('email.inactive')}
                          />
                        </div>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                        {tpl.slug}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </aside>

        {/* ── RIGHT PANEL ── */}
        <main className="flex-1 min-w-0">
          {!selectedSlug ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
              {t('email.selectTemplate')}
            </div>
          ) : templateLoading ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
              {t('common.loading')}
            </div>
          ) : !selectedTemplate ? null : (
            <>
              {/* Panel header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold">{t('email.editTemplate')}</h2>
                  <Badge variant="outline" className="font-mono text-xs">
                    {selectedTemplate.slug}
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  {missingRequired.length > 0 && (
                    <span className="text-xs text-destructive">
                      ⚠️ {t('email.missingVariables')}: {missingRequired.map(v => v.key).join(', ')}
                    </span>
                  )}
                  <Button onClick={handleSave} disabled={updateMutation.isPending} size="sm">
                    {updateMutation.isPending ? t('email.saving') : t('email.save')}
                  </Button>
                </div>
              </div>

              {/* Subject field */}
              <Card className="mb-4">
                <CardHeader className="py-3 px-4 pb-0">
                  <CardTitle className="text-sm font-medium">{t('email.subject')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-2 px-4 pb-3">
                  <Input
                    value={editedSubject}
                    onChange={e => setEditedSubject(e.target.value)}
                    className="text-sm"
                  />
                </CardContent>
              </Card>

              {/* Block sections */}
              {BLOCK_KEYS.map(key => {
                const block = selectedTemplate.blocks[key];
                if (!block) return null;
                return (
                  <BlockEditorRow
                    key={key}
                    blockKey={key}
                    content={editedBlocks[key] ?? block.content}
                    editable={block.editable}
                    variables={selectedTemplate.variables}
                    onChange={handleBlockChange}
                  />
                );
              })}

              {/* Status bar */}
              <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground border-t pt-3">
                <span>
                  {t('email.source')}:{' '}
                  {selectedTemplate.source === 'db' ? t('email.customized') : t('email.default')}
                </span>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
